import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useRaisedFundsSummary from '../hooks/useRaisedFundsSummary';
import './PowerHourGlow.css';

/**
 * Full-screen glow shown while a Power Boost is running.
 *
 * Dismissal is tracked per boost key rather than as a single flag. This
 * component is mounted once in Layout and never remounts, so a plain boolean
 * meant that dismissing the glow for one boost also suppressed it for every
 * later boost in the same session — invisible with a single boost, wrong as
 * soon as there can be several.
 */
const PowerHourGlow: React.FC = () => {
  const { data } = useRaisedFundsSummary();
  const [dismissedKeys, setDismissedKeys] = useState<string[]>([]);
  const [showText, setShowText] = useState(true);
  const navigate = useNavigate();

  const activeBoosts = useMemo(
    () =>
      (data?.funds || []).filter(
        (fund) => fund.type === 'power-hour-match' && fund.status === 'active' && !fund.capReached
      ),
    [data?.funds]
  );

  // A boost the viewer has already dismissed stays dismissed; one that starts
  // afterwards brings the glow back.
  const liveBoosts = useMemo(
    () => activeBoosts.filter((fund) => !dismissedKeys.includes(fund.key)),
    [activeBoosts, dismissedKeys]
  );

  // Stable identity for "which boosts are currently showing", so the banner
  // re-announces when that set changes instead of staying hidden forever.
  const bannerKey = useMemo(
    () => liveBoosts.map((fund) => fund.key).sort().join('|'),
    [liveBoosts]
  );

  useEffect(() => {
    if (!bannerKey) return;
    setShowText(true);
  }, [bannerKey]);

  useEffect(() => {
    if (!bannerKey || !showText) return undefined;
    const timer = setTimeout(() => setShowText(false), 10000);
    return () => clearTimeout(timer);
  }, [bannerKey, showText]);

  const handleStarClick = useCallback(() => {
    navigate('/raised');
  }, [navigate]);

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Dismiss exactly the boosts running right now, so a later one still shows.
      setDismissedKeys((prev) => Array.from(new Set([...prev, ...activeBoosts.map((fund) => fund.key)])));
    },
    [activeBoosts]
  );

  if (liveBoosts.length === 0) return null;

  const bannerText = liveBoosts.length > 1 ? 'POWER BOOSTS ACTIVE' : 'POWER BOOST ACTIVE';

  return (
    <>
      <div className="power-hour-glow-border" />
      {showText && <div className="power-hour-text">{bannerText}</div>}
      <div className="power-hour-corner">
        <button className="power-hour-star" onClick={handleStarClick} aria-label="Go to matching donors">
          ⭐
        </button>
        <button className="power-hour-dismiss" onClick={handleDismiss} aria-label="Dismiss power boost effect">
          ✕
        </button>
      </div>
    </>
  );
};

export default PowerHourGlow;
